/**
 * Tenant administration (C05-001..004): directory + suspend, consented read-only impersonation, diagnostics, limits + agent reset.
 * Partner client privacy (pending item 22): a partner-owned client workspace arrives masked (opaque id, partner, plan, MRR);
 * the directory renders it as such, and a "By partner" tab shows the per-partner summary (its reads are audited server-side).
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { FAILURE_KINDS, IMPERSONATION_RESOURCES, USAGE_METRICS } from '@qm/contracts/admin';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, DataTable, ErrorNote, Field, Loadable, Money, Notice, ReasonForm, Screen, Stat, Tabs, When } from '../kit/ui';
import { isMaskedTenant, type DirectTenantDetail, type ImpersonationSession, type MaskedTenantRow, type TenantDetail } from '../api';
import { canOperate, canSeeRevenue, canSupport, impersonationLeft, isSuperAdmin } from '../lib/logic';
import { useAdminApi } from './layout';
import { ViewAsAction } from './view-as';

export function TenantsPage() {
  const { t } = useSurface();
  const [sp, setSp] = useSearchParams();
  const view = sp.get('view') === 'partners' ? 'partners' : 'all';
  return (
    <Screen title={t('portal.admin.tenants.title')} testId="tenants">
      <Tabs label={t('portal.admin.tenants.title')} active={view} onSelect={(v) => setSp(v === 'partners' ? { view: 'partners' } : {})}
        tabs={[{ id: 'all', label: t('portal.admin.tenants.view.all') }, { id: 'partners', label: t('portal.admin.tenants.view.partners') }]} />
      {view === 'partners' ? <PartnerClientSummaryView /> : <TenantDirectory />}
    </Screen>
  );
}

function TenantDirectory() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const [sp, setSp] = useSearchParams();
  const q = sp.get('q') ?? '';
  const automation = sp.get('automation') ?? '';
  const list = useResource(`tenants:${q}:${automation}`, () => api.tenants({ ...(q ? { q } : {}), ...(automation ? { automation } : {}) }));
  const revenue = canSeeRevenue(roles);
  return (
    <>
      <form className="qx-toolbar" onSubmit={(e) => { e.preventDefault(); const v = String(new FormData(e.currentTarget).get('q') ?? ''); setSp(v ? { q: v } : {}); }}>
        <Field label={t('portal.admin.search')} hint={t('portal.admin.tenants.searchHint')}>{(id, describedBy) => <input id={id} name="q" type="search" defaultValue={q} aria-describedby={describedBy} />}</Field>
        <Field label={t('portal.admin.tenants.automation')}>{(id) => <select id={id} value={automation} onChange={(e) => setSp(e.target.value ? { automation: e.target.value } : {})}><option value="">{t('portal.admin.any')}</option><option value="running">{t('portal.admin.tenants.running')}</option><option value="suspended">{t('portal.admin.tenants.suspended')}</option></select>}</Field>
      </form>
      <Loadable resource={list}>
        {(env) => <DataTable rows={env.data} rowKey={(r) => r.id} empty={t('portal.kit.empty')} columns={[
          { key: 'n', header: t('portal.admin.tenants.tenant'), render: (r) => (isMaskedTenant(r)
            ? <span data-testid="masked-tenant"><Link to={`/tenants/${encodeURIComponent(r.id)}`}><code>{r.opaqueId}</code></Link> <Badge>{t('portal.admin.tenants.partnerClient')}</Badge> <Text tone="muted">{t('portal.admin.tenants.viaPartner', { partner: r.partner.name ?? r.partner.id })}</Text></span>
            : <Link to={`/tenants/${encodeURIComponent(r.id)}`}>{r.name}</Link>) },
          { key: 'p', header: t('portal.admin.customers.plan'), render: (r) => r.plan ?? '-' },
          ...(revenue ? [{ key: 'm', header: t('portal.admin.tenants.mrr'), render: (r: (typeof env.data)[number]) => (r.mrr ? <Money minor={r.mrr.amountMinor} currency={r.mrr.currency} /> : '-') }] : []),
          { key: 'u', header: t('portal.admin.tenants.messages'), render: (r) => (isMaskedTenant(r) ? '-' : `${r.usage.messagesMonth.used}${r.usage.messagesMonth.limit !== null ? ` / ${r.usage.messagesMonth.limit}` : ''}`) },
          { key: 'a', header: t('portal.admin.tenants.automation'), render: (r) => (isMaskedTenant(r) ? '-' : <Badge tone={r.automation === 'suspended' ? 'bad' : 'good'}>{t(`portal.admin.tenants.${r.automation}`)}</Badge>) },
          { key: 's', header: t('portal.admin.status'), narrow: 'hide', render: (r) => r.status },
        ]} />}
      </Loadable>
      <Text tone="muted">{t('portal.admin.tenants.privacyNote')}</Text>
    </>
  );
}

/** Per-partner summary of partner-owned client workspaces: partner, client count, plans, MRR totals. No client is named. */
export function PartnerClientSummaryView() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const summary = useResource('partnerClients:summary', () => api.partnerClientSummary());
  const revenue = canSeeRevenue(roles);
  return (
    <section data-testid="partner-summary">
      <Text tone="muted">{t('portal.admin.tenants.summaryNote')}</Text>
      <Loadable resource={summary}>
        {(rows) => <DataTable rows={rows} rowKey={(r) => r.partner.id} empty={t('portal.admin.tenants.summaryEmpty')} columns={[
          { key: 'p', header: t('portal.admin.tenants.partner'), render: (r) => r.partner.name ?? r.partner.id },
          { key: 'c', header: t('portal.admin.tenants.clientCount'), render: (r) => String(r.clientCount) },
          { key: 'l', header: t('portal.admin.tenants.plans'), render: (r) => r.plans.map((p) => `${p.plan ?? t('portal.admin.tenants.noPlan')} × ${p.count}`).join(', ') },
          ...(revenue ? [{ key: 'm', header: t('portal.admin.tenants.mrrTotal'), render: (r: (typeof rows)[number]) => (r.mrrTotals.length ? <>{r.mrrTotals.map((m) => <span key={m.currency}><Money minor={m.amountMinor} currency={m.currency} /> </span>)}</> : '-') }] : []),
        ]} />}
      </Loadable>
    </section>
  );
}

export function TenantDetailPage() {
  const api = useAdminApi();
  const { id = '' } = useParams();
  const d = useResource(`tenant:${id}`, () => api.tenant(id));
  return <Loadable resource={d}>{(v) => <TenantDetailView tenant={v} onChanged={d.reload} />}</Loadable>;
}

export function TenantDetailView({ tenant, onChanged }: { tenant: TenantDetail; onChanged: () => void }) {
  return isMaskedTenant(tenant) ? <MaskedTenantView tenant={tenant} /> : <DirectTenantView tenant={tenant} onChanged={onChanged} />;
}

/** A partner client: opaque id, partner and billing only. Anything more needs the client's consent (impersonation). */
function MaskedTenantView({ tenant }: { tenant: MaskedTenantRow }) {
  const { t, roles } = useSurface();
  return (
    <Screen title={t('portal.admin.tenants.partnerClient')} subtitle={tenant.opaqueId} testId="tenant-detail">
      <div className="qx-grid">
        <Stat label={t('portal.admin.tenants.partner')} value={tenant.partner.name ?? tenant.partner.id} />
        <Stat label={t('portal.admin.customers.plan')} value={tenant.plan ?? '-'} />
        {canSeeRevenue(roles) && tenant.mrr ? <Stat testId="tenant-mrr" label={t('portal.admin.tenants.mrr')} value={<Money minor={tenant.mrr.amountMinor} currency={tenant.mrr.currency} />} /> : null}
        <Stat label={t('portal.admin.status')} value={tenant.status} />
      </div>
      <Notice testId="partner-client-private">{t('portal.admin.tenants.privateDetail')}</Notice>
      <ViewAsAction kind="customer" target={{ tenantId: tenant.opaqueId }} />
      {canSupport(roles) ? <Link to={`/impersonation?tenant=${encodeURIComponent(tenant.opaqueId)}`}>{t('portal.admin.impersonation.start')}</Link> : null}
    </Screen>
  );
}

function DirectTenantView({ tenant, onChanged }: { tenant: DirectTenantDetail; onChanged: () => void }) {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const suspend = useAction((reason: string) => api.suspendAutomation(tenant.id, reason));
  const resume = useAction((reason: string) => api.resumeAutomation(tenant.id, reason));
  const [limits, setLimits] = useState<Record<string, string>>(Object.fromEntries(USAGE_METRICS.map((m) => [m, tenant.limits[m] === undefined ? '' : String(tenant.limits[m])])));
  const saveLimits = useAction((reason: string) => api.setLimits(tenant.id, Object.fromEntries(Object.entries(limits).filter(([, v]) => v !== '').map(([k, v]) => [k, Number(v)])), reason));
  const [agentKey, setAgentKey] = useState('whatsapp.agent');
  const reset = useAction((reason: string) => api.resetAgent(tenant.id, agentKey, reason));
  const done = async (p: Promise<unknown>) => { if (await p) onChanged(); };
  return (
    <Screen title={tenant.name} subtitle={tenant.id} testId="tenant-detail">
      {isSuperAdmin(roles) ? <Link to={`/growth-audit?tenant=${encodeURIComponent(tenant.id)}`}>{t('growth-audit.control.title')}</Link> : null}
      <div className="qx-grid">
        <Stat label={t('portal.admin.customers.plan')} value={tenant.plan ?? '-'} />
        {canSeeRevenue(roles) && tenant.mrr ? <Stat testId="tenant-mrr" label={t('portal.admin.tenants.mrr')} value={<Money minor={tenant.mrr.amountMinor} currency={tenant.mrr.currency} />} /> : null}
        <Stat label={t('portal.admin.tenants.automation')} value={<Badge tone={tenant.automation === 'suspended' ? 'bad' : 'good'} testId={`automation-${tenant.automation}`}>{t(`portal.admin.tenants.${tenant.automation}`)}</Badge>} />
      </div>
      {canOperate(roles) ? (
        <Panel>
          <Heading level={2}>{tenant.automation === 'suspended' ? t('portal.admin.tenants.resume') : t('portal.admin.tenants.suspend')}</Heading>
          {tenant.automation === 'suspended'
            ? <ReasonForm testId="resume-automation" label={t('portal.kit.reason')} submitLabel={t('portal.admin.tenants.resume')} pending={resume.pending} onSubmit={(r) => done(resume.run(r))} />
            : <ReasonForm testId="suspend-automation" danger label={t('portal.kit.reason')} submitLabel={t('portal.admin.tenants.suspend')} pending={suspend.pending} onSubmit={(r) => done(suspend.run(r))} />}
          <ErrorNote error={suspend.error ?? resume.error} />
        </Panel>
      ) : null}
      {isSuperAdmin(roles) ? (
        <>
          <Panel data-testid="usage-limits">
            <Heading level={2}>{t('portal.admin.tenants.limits')}</Heading>
            {USAGE_METRICS.map((m) => <Field key={m} label={t(`portal.admin.tenants.metric.${m}`)}>{(id) => <input id={id} type="number" min="0" value={limits[m] ?? ''} onChange={(e) => setLimits((x) => ({ ...x, [m]: e.target.value }))} />}</Field>)}
            <ReasonForm label={t('portal.kit.reason')} submitLabel={t('portal.admin.save')} pending={saveLimits.pending} onSubmit={(r) => done(saveLimits.run(r))} />
            <Text tone="muted">{t('portal.admin.tenants.atomic')}</Text>
            <ErrorNote error={saveLimits.error} />
          </Panel>
          <Panel data-testid="agent-reset">
            <Heading level={2}>{t('portal.admin.tenants.agentReset')}</Heading>
            <Field label={t('portal.admin.tenants.agentKey')}>{(id) => <input id={id} value={agentKey} onChange={(e) => setAgentKey(e.target.value)} />}</Field>
            <Text tone="muted">{t('portal.admin.tenants.agentResetNote')}</Text>
            <ReasonForm danger label={t('portal.kit.reason')} submitLabel={t('portal.admin.tenants.reset')} pending={reset.pending} onSubmit={(r) => done(reset.run(r))} />
            {reset.result ? <Notice testId="agent-reset-done">{t('portal.admin.tenants.resetDone', { v: reset.result.version })}</Notice> : null}
            <ErrorNote error={reset.error} />
          </Panel>
        </>
      ) : null}
      <ViewAsAction kind="customer" target={{ tenantId: tenant.id }} />
      {canSupport(roles) ? <Link to={`/impersonation?tenant=${encodeURIComponent(tenant.id)}`}>{t('portal.admin.impersonation.start')}</Link> : null}
      <Link to={`/diagnostics?tenant=${encodeURIComponent(tenant.id)}`}>{t('portal.admin.nav.diagnostics')}</Link>
    </Screen>
  );
}

/** Read-only, consented, time-boxed impersonation with a persistent banner; no write controls exist here (C05-002). */
export function ImpersonationPage() {
  const { t, now } = useSurface();
  const api = useAdminApi();
  const [sp] = useSearchParams();
  const [tenantId, setTenantId] = useState(sp.get('tenant') ?? '');
  const [session, setSession] = useState<ImpersonationSession | null>(null);
  const [resource, setResource] = useState<string>('summary');
  const start = useAction((reason: string) => api.startImpersonation(tenantId.trim(), reason));
  const end = useAction(() => (session ? api.endImpersonation(session.id) : Promise.resolve(null)));
  const [, tick] = useState(0);
  useEffect(() => { if (!session) return; const h = setInterval(() => tick((n) => n + 1), 15_000); return () => clearInterval(h); }, [session]);
  const left = session ? impersonationLeft(session.expiresAt, now()) : 0;
  const expired = !!session && left === 0;
  return (
    <Screen title={t('portal.admin.impersonation.title')} subtitle={t('portal.admin.impersonation.subtitle')} testId="impersonation">
      {!session ? (
        <Panel>
          <Field label={t('portal.admin.impersonation.tenant')}>{(id) => <input id={id} value={tenantId} onChange={(e) => setTenantId(e.target.value)} />}</Field>
          <ReasonForm testId="impersonation-start" label={t('portal.kit.reason')} submitLabel={t('portal.admin.impersonation.start')} pending={start.pending} onSubmit={async (r) => { const s = await start.run(r); if (s) setSession(s); }} />
          <Text tone="muted">{t('portal.admin.impersonation.consentNote')}</Text>
          <ErrorNote error={start.error} />
        </Panel>
      ) : expired ? (
        <Notice tone="bad" testId="impersonation-expired">{t('portal.admin.impersonation.expired')}</Notice>
      ) : (
        <>
          <div className="qx-banner" role="status" data-testid="impersonation-banner">{t('portal.admin.impersonation.banner', { tenant: session.tenantId, min: Math.ceil(left / 60_000) })}</div>
          <Tabs label={t('portal.admin.impersonation.title')} active={resource} onSelect={setResource} tabs={IMPERSONATION_RESOURCES.map((r) => ({ id: r, label: t(`portal.admin.impersonation.resource.${r}`) }))} />
          <ImpersonationView id={session.id} resource={resource} />
          <Button onClick={async () => { await end.run(); setSession(null); }}>{t('portal.admin.impersonation.end')}</Button>
        </>
      )}
    </Screen>
  );
}

function ImpersonationView({ id, resource }: { id: string; resource: string }) {
  const api = useAdminApi();
  const v = useResource(`imp:${id}:${resource}`, () => api.impersonationView(id, resource));
  return <Loadable resource={v}>{(data) => <pre className="qx-secret" data-testid="impersonation-view">{JSON.stringify(data, null, 2)}</pre>}</Loadable>;
}

/** Failed messages, webhooks and templates by tenant, phone and time, with safe (idempotent) retry (C05-003). */
export function DiagnosticsPage() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const [sp, setSp] = useSearchParams();
  const tenantId = sp.get('tenant') ?? '';
  const kind = sp.get('kind') ?? '';
  const phone = sp.get('phone') ?? '';
  const from = sp.get('from') ?? '';
  const key = `failures:${tenantId}:${kind}:${phone}:${from}`;
  const list = useResource(key, () => (tenantId ? api.failures({ tenantId, ...(kind ? { kind } : {}), ...(phone ? { phone } : {}), ...(from ? { from: new Date(from).toISOString() } : {}) }) : Promise.resolve([])));
  const [retrying, setRetrying] = useState<string | null>(null);
  const retry = useAction((id: string, reason: string) => api.retryFailure(id, tenantId, reason));
  return (
    <Screen title={t('portal.admin.diagnostics.title')} testId="diagnostics">
      <form className="qx-toolbar" onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.currentTarget); const n = new URLSearchParams(); for (const k of ['tenant', 'kind', 'phone', 'from']) { const v = String(f.get(k) ?? ''); if (v) n.set(k, v); } setSp(n); }}>
        <Field label={t('portal.admin.impersonation.tenant')}>{(id) => <input id={id} name="tenant" defaultValue={tenantId} required />}</Field>
        <Field label={t('portal.admin.diagnostics.kind')}>{(id) => <select id={id} name="kind" defaultValue={kind}><option value="">{t('portal.admin.any')}</option>{FAILURE_KINDS.map((k) => <option key={k} value={k}>{t(`portal.admin.diagnostics.kind.${k}`)}</option>)}</select>}</Field>
        <Field label={t('portal.admin.diagnostics.phone')}>{(id) => <input id={id} name="phone" type="tel" defaultValue={phone} />}</Field>
        <Field label={t('portal.admin.diagnostics.from')}>{(id) => <input id={id} name="from" type="datetime-local" defaultValue={from} />}</Field>
        <Button type="submit" variant="primary">{t('portal.admin.search')}</Button>
      </form>
      <Loadable resource={list}>
        {(rows) => <DataTable rows={rows} rowKey={(f) => f.id} empty={tenantId ? t('portal.admin.diagnostics.none') : t('portal.admin.diagnostics.enterTenant')} columns={[
          { key: 'k', header: t('portal.admin.diagnostics.kind'), render: (f) => t(`portal.admin.diagnostics.kind.${f.kind}`) },
          { key: 'w', header: t('portal.admin.diagnostics.when'), render: (f) => <When iso={f.occurredAt} /> },
          { key: 'e', header: t('portal.admin.diagnostics.error'), render: (f) => <><code>{f.errorCode}</code> {f.errorDetail ?? ''}</> },
          { key: 'r', header: t('portal.admin.diagnostics.ref'), narrow: 'hide', render: (f) => <code>{f.sourceRef}</code> },
          { key: 'a', header: '', render: (f) => (f.retriedAt ? <Badge testId="retried">{t('portal.admin.diagnostics.retried')}</Badge> : f.retryable && canSupport(roles) ? <Button onClick={() => setRetrying(f.id)}>{t('portal.admin.diagnostics.retry')}</Button> : null) },
        ]} />}
      </Loadable>
      {retrying ? (
        <Panel data-testid="retry-form">
          <Text tone="muted">{t('portal.admin.diagnostics.retryNote')}</Text>
          <ReasonForm label={t('portal.kit.reason')} submitLabel={t('portal.admin.diagnostics.retry')} pending={retry.pending} onSubmit={async (r) => { if (await retry.run(retrying, r)) { setRetrying(null); list.reload(); } }} />
        </Panel>
      ) : null}
      <ErrorNote error={retry.error} />
    </Screen>
  );
}

